<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { Head, Link } from '@inertiajs/vue3';

const kecamatans = ref([]);
const loading = ref(true);
const saving = ref(false);
const isEditing = ref(false);
const processingStatusId = ref(null);

const form = ref({
    id: null,
    nama_kecamatan: '',
    slug: '',
    deskripsi: '',
    artikel: '',
    is_active: true,
    meta_title: '',
    meta_description: '',
    meta_keywords: ''
});

const originalData = ref(null);

// Modal Logic
const errorModalContent = ref([]);
const modalType = ref('error');

const showModal = (type, messages) => {
    modalType.value = type;
    errorModalContent.value = Array.isArray(messages) ? messages : [messages];
    const modal = new bootstrap.Modal(document.getElementById('errorModal'));
    modal.show();
};

const formErrors = ref({}); // To store validation errors

// Methods
const loadKecamatans = async () => {
    loading.value = true;
    try {
        const response = await window.axios.get('/admin/kecamatan/get');
        if (response.data.status === 'success') {
            kecamatans.value = response.data.data;
        }
    } catch (error) {
        console.error('Error:', error);
    } finally {
        loading.value = false;
    }
};
// CKEditor Instances
let deskripsiEditor = null;
let artikelEditor = null;

const initEditors = () => {
    if (typeof CKEDITOR === 'undefined') {
        console.error('CKEditor not loaded');
        return;
    }

    if (CKEDITOR.instances['deskripsi']) {
        try { CKEDITOR.instances['deskripsi'].destroy(); } catch (e) {}
    }
    if (CKEDITOR.instances['artikel']) {
        try { CKEDITOR.instances['artikel'].destroy(); } catch (e) {}
    }

    // Deskripsi Editor
    deskripsiEditor = CKEDITOR.replace('deskripsi', {
        height: 100,
        toolbar: [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'links', items: ['Link', 'Unlink'] }
        ]
    });
    deskripsiEditor.on('change', () => {
        form.value.deskripsi = deskripsiEditor.getData();
    });

    // Artikel Editor
    artikelEditor = CKEDITOR.replace('artikel', {
        height: 300,
        toolbar: [
            { name: 'styles', items: ['Format'] },
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'links', items: ['Link', 'Unlink'] }
        ],
        format_tags: 'p;h1;h2;h3;h4;h5;h6'
    });
    artikelEditor.on('change', () => {
        form.value.artikel = artikelEditor.getData();
    });
};

onBeforeUnmount(() => {
    if (typeof CKEDITOR !== 'undefined') {
        if (CKEDITOR.instances['deskripsi']) try { CKEDITOR.instances['deskripsi'].destroy(); } catch (e) {}
        if (CKEDITOR.instances['artikel']) try { CKEDITOR.instances['artikel'].destroy(); } catch (e) {}
    }
});

// Methods removed: openModal, openMetaModal
// New methods: edit, resetForm




const slugify = (text) => {
  return text.toString().toLowerCase().trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

watch(() => form.value.nama_kecamatan, (newVal) => {
    if (!isEditing.value && newVal) {
        form.value.slug = slugify(newVal);
    }
});

const submitForm = async () => {
    saving.value = true;
    await new Promise(resolve => setTimeout(resolve, 300));

    // Update data from editors
    if (deskripsiEditor) form.value.deskripsi = deskripsiEditor.getData();
    if (artikelEditor) form.value.artikel = artikelEditor.getData();

    // Client-side Validation (Error Modal)
    formErrors.value = {};
    const errors = [];
    
    if (!form.value.nama_kecamatan || form.value.nama_kecamatan.trim() === '') {
        errors.push('Nama Kecamatan wajib diisi');
    }
    if (!form.value.deskripsi || form.value.deskripsi.trim() === '') {
        errors.push('Deskripsi wajib diisi');
    }
    if (!form.value.artikel || form.value.artikel.trim() === '') {
        errors.push('Artikel wajib diisi');
    }

    if (errors.length > 0) {
        saving.value = false;
        showModal('error', errors);
        return;
    }

    let url = '/admin/kecamatan';
    const data = { ...form.value };
    
    // Check for changes if editing
    if (isEditing.value) {
        const currentData = {
            nama_kecamatan: form.value.nama_kecamatan,
            slug: form.value.slug,
            deskripsi: form.value.deskripsi,
            artikel: form.value.artikel,
            // Removed is_active
            meta_title: form.value.meta_title,
            meta_description: form.value.meta_description,
            meta_keywords: form.value.meta_keywords
        };
        
        if (originalData.value && JSON.stringify(currentData) === JSON.stringify(originalData.value)) {
            saving.value = false;
            showModal('warning', ['Tidak ada perubahan data yang disimpan.']);
            return;
        }

        if (!form.value.id) {
            alert('Error: ID kecamatan tidak ditemukan. Silakan refresh halaman.');
            saving.value = false;
            return;
        }
        data._method = 'PUT';
        url = `/admin/kecamatan/${form.value.id}`;
    }
    
    try {
        const response = await window.axios.post(url, data);
        if (response.data.status === 'success') {
            resetForm();
            loadKecamatans();
        } else if (response.data.status === 'info') {
            modalType.value = 'warning';
            errorModalContent.value = [response.data.message];
            const modal = new bootstrap.Modal(document.getElementById('errorModal'));
            modal.show();
        }
    } catch (error) {
        console.error(error);
        if (error.response && error.response.data) {
            if (error.response.status === 422) {
                // Collect validation errors
                const serverErrors = [];
                if (error.response.data.errors) {
                    Object.values(error.response.data.errors).forEach(err => {
                        serverErrors.push(...err);
                    });
                } else if (error.response.data.message) {
                    serverErrors.push(error.response.data.message);
                }
                showModal('error', serverErrors.length > 0 ? serverErrors : ['Validasi gagal.']);
            } else {
                showModal('error', [error.response.data.message || 'Terjadi kesalahan pada server.']);
            }
        } else {
            showModal('error', ['Terjadi kesalahan yang tidak diketahui.']);
        }
    } finally {
        saving.value = false;
    }
};

const edit = (item) => {
    // Debugging: Check if item has ID
    if (!item.id) {
        console.error('Edit error: Item has no ID', item);
        alert('Data kecamatan tidak valid (ID missing).');
        return;
    }

    form.value = {
        id: item.id,
        nama_kecamatan: item.nama_kecamatan,
        slug: item.slug || '',
        deskripsi: item.deskripsi || '',
        artikel: item.artikel || '',
        is_active: item.is_active,
        meta_title: item.meta_title || '',
        meta_description: item.meta_description || '',
        meta_keywords: item.meta_keywords || ''
    };
    isEditing.value = true;
    
    // Set editor data
    if (deskripsiEditor) {
        deskripsiEditor.setData(item.deskripsi || '', {
            callback: function() {
                if (originalData.value) originalData.value.deskripsi = this.getData();
            }
        });
    }
    if (artikelEditor) {
        artikelEditor.setData(item.artikel || '', {
            callback: function() {
                if (originalData.value) originalData.value.artikel = this.getData();
            }
        });
    }
    
    // Store original data for comparison
    originalData.value = {
        nama_kecamatan: item.nama_kecamatan,
        slug: item.slug || '',
        deskripsi: item.deskripsi || '', // Will be updated by callback
        artikel: item.artikel || '',     // Will be updated by callback
        // Removed is_active since it's not in the form
        meta_title: item.meta_title || '',
        meta_description: item.meta_description || '',
        meta_keywords: item.meta_keywords || ''
    };
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const resetForm = () => {
    isEditing.value = false;
    originalData.value = null; // Reset comparison data
    form.value = {
        id: null,
        nama_kecamatan: '',
        slug: '',
        deskripsi: '',
        artikel: '',
        is_active: true,
        meta_title: '',
        meta_description: '',
        meta_keywords: ''
    };
    isEditing.value = false;
    formErrors.value = {};
    if (deskripsiEditor) deskripsiEditor.setData('');
    if (artikelEditor) artikelEditor.setData('');
};

// Detail Modal Logic
const detailModalTitle = ref('');
const detailModalContent = ref('');

const viewDetail = (title, content) => {
    detailModalTitle.value = title;
    detailModalContent.value = content || '<em>Tidak ada data.</em>';
    const modal = new bootstrap.Modal(document.getElementById('detailModal'));
    modal.show();
};

const toggleStatus = async (kecamatan) => {
    processingStatusId.value = kecamatan.id;
    try {
        const response = await window.axios.patch(`/admin/kecamatan/${kecamatan.id}/toggle-status`);
        if (response.data.status === 'success') {
            const index = kecamatans.value.findIndex(k => k.id === kecamatan.id);
            if (index !== -1) {
                kecamatans.value[index].is_active = !kecamatans.value[index].is_active;
            }
        }
    } catch (error) {
        showModal('error', ['Gagal mengubah status.']);
    } finally {
        processingStatusId.value = null;
    }
};

// Delete Logic
const deleteId = ref(null);
const deleteName = ref('');
const deleteConfirmedCheck = ref(false);

const confirmDelete = (item) => {
    deleteId.value = item.id;
    deleteName.value = item.nama_kecamatan;
    deleteConfirmedCheck.value = false;
    const modal = new bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
};

const submitDelete = async () => {
    if (!deleteId.value) return;
    try {
        await window.axios.delete(`/admin/kecamatan/${deleteId.value}`);
        // Hide modal
        const modalEl = document.getElementById('deleteModal');
        const modal = bootstrap.Modal.getInstance(modalEl);
        if (modal) modal.hide();
        
        loadKecamatans();
    } catch (error) {
        showModal('error', ['Gagal menghapus data.']);
        // Hide modal even on error to show error modal
         const modalEl = document.getElementById('deleteModal');
        const modal = bootstrap.Modal.getInstance(modalEl);
        if (modal) modal.hide();
    }
};

onMounted(() => {
    loadKecamatans();
    nextTick(() => {
        initEditors();
    });
});
</script>

<template>
    <Head title="Kelola Kecamatan" />

    <div class="container-full">
        <!-- Breadcrumb Section - Updated 2026-01-18 23:40 -->
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola Kecamatan</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item"><Link href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></Link></li>
                                <li class="breadcrumb-item active" aria-current="page">Kecamatan</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Left Column: Form -->
                <div class="col-lg-5 col-12 mb-4">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary">
                                <i class="ti-plus me-2" v-if="!isEditing"></i>
                                <i class="ti-pencil me-2" v-else></i>
                                {{ isEditing ? 'Edit Kecamatan' : 'Tambah Kecamatan' }}
                            </h4>
                        </div>
                        <form @submit.prevent="submitForm">
                            <div class="box-body">
                                <div class="form-group mb-3">
                                    <label class="form-label" for="nama_kecamatan">Nama Kecamatan <span class="text-danger">*</span></label>
                                    <input type="text" id="nama_kecamatan" v-model="form.nama_kecamatan" class="form-control" :class="{ 'is-invalid': formErrors.nama_kecamatan }" placeholder="Nama kecamatan" autocomplete="off">
                                    <div class="invalid-feedback d-block" v-if="formErrors.nama_kecamatan">{{ formErrors.nama_kecamatan }}</div>
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label" for="slug">Slug</label>
                                    <input type="text" id="slug" v-model="form.slug" class="form-control" placeholder="slug-kecamatan" autocomplete="off">
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label" for="deskripsi">Deskripsi <span class="text-danger">*</span></label>
                                    <textarea id="deskripsi" name="deskripsi" class="form-control" :class="{ 'is-invalid': formErrors.deskripsi }"></textarea>
                                    <div class="invalid-feedback d-block" v-if="formErrors.deskripsi">{{ formErrors.deskripsi }}</div>
                                </div>
                                <div class="form-group mb-3">
                                    <label class="form-label" for="artikel">Artikel <span class="text-danger">*</span></label>
                                    <textarea id="artikel" name="artikel" class="form-control" :class="{ 'is-invalid': formErrors.artikel }"></textarea>
                                    <div class="invalid-feedback d-block" v-if="formErrors.artikel">{{ formErrors.artikel }}</div>
                                </div>

                                <!-- SEO Meta Data Card -->
                                <div class="card bg-light-light mb-0 mt-3">
                                    <div class="card-body py-3">
                                        <h5 class="text-primary mb-3"><i class="ti-world me-2"></i>SEO Meta Data</h5>
                                        <div class="form-group mb-3">
                                            <label class="form-label" for="meta_title">Meta Title</label>
                                            <input type="text" id="meta_title" name="meta_title" v-model="form.meta_title" class="form-control" placeholder="Judul pencarian Google">
                                        </div>
                                        <div class="form-group mb-3">
                                            <label class="form-label" for="meta_description">Meta Description</label>
                                            <textarea id="meta_description" name="meta_description" v-model="form.meta_description" class="form-control" rows="3" placeholder="Deskripsi di hasil pencarian"></textarea>
                                        </div>
                                        <div class="form-group mb-0">
                                            <label class="form-label" for="meta_keywords">Meta Keywords</label>
                                            <input type="text" id="meta_keywords" name="meta_keywords" v-model="form.meta_keywords" class="form-control" placeholder="Kata kunci, pisahkan dengan koma">
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="box-footer text-end">
                                <button v-if="isEditing" type="button" class="btn btn-warning me-2" @click="resetForm" :disabled="saving">
                                    <i class="ti-trash me-2"></i> Batal
                                </button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                                    <i v-else class="ti-save-alt me-2"></i>
                                    {{ saving ? 'Menyimpan...' : (isEditing ? 'Simpan Perubahan' : 'Simpan') }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <!-- Right Column: Table -->
                <div class="col-lg-7 col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary"><i class="ti-view-list me-2"></i>Daftar Kecamatan</h4>
                        </div>
                        <div class="box-body">
                            <div class="table-responsive">
                                <table class="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Nama Kecamatan</th>
                                            <th class="text-nowrap" style="width: 1%;">Artikel</th>
                                            <th class="text-nowrap" style="width: 1%;">Status</th>
                                            <th class="text-center text-nowrap" style="width: 1%;">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="loading"><td colspan="5" class="text-center p-5"><div class="spinner-border text-primary"></div></td></tr>
                                        <tr v-if="!loading && kecamatans.length === 0">
                                            <td colspan="5" class="text-center py-4">Belum ada data kecamatan.</td>
                                        </tr>
                                        <tr v-else v-for="(item, index) in kecamatans" :key="item.id" class="align-middle">
                                            <td>{{ index + 1 }}</td>
                                            <td>
                                                <div class="fw-bold">{{ item.nama_kecamatan }}</div>
                                                <small class="text-muted d-block mt-1" v-if="item.deskripsi">
                                                    {{ item.deskripsi.replace(/<[^>]*>?/gm, '').substring(0, 100) }}{{ item.deskripsi.length > 100 ? '...' : '' }}
                                                </small>
                                            </td>
                                            <td class="text-nowrap">
                                                <button type="button" class="btn btn-success btn-sm" @click="viewDetail('Artikel Kecamatan', item.artikel)" title="Lihat Artikel"><i class="fa fa-file-text"></i> Lihat</button>
                                            </td>
                                            <td class="text-nowrap">
                                                <button type="button" class="btn btn-sm rounded-1 px-3" :class="item.is_active ? 'btn-success' : 'btn-danger'" @click="toggleStatus(item)" :disabled="processingStatusId === item.id" title="Toggle Status">
                                                    <span v-if="processingStatusId === item.id" class="spinner-border spinner-border-sm me-1"></span>
                                                    <i v-else class="fa me-1" :class="item.is_active ? 'fa-check-circle' : 'fa-times-circle'"></i>
                                                    {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
                                                </button>
                                            </td>
                                            <td class="text-center text-nowrap">
                                                <div class="btn-group">
                                                    <button class="btn btn-primary btn-sm" @click="edit(item)" title="Edit"><i class="fa fa-edit"></i></button>
                                                    <button class="btn btn-danger btn-sm" @click="confirmDelete(item)" title="Hapus"><i class="fa fa-trash"></i></button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <!-- Detail Modal -->
        <div class="modal fade" id="detailModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header bg-primary text-white">
                        <h5 class="modal-title"><i class="fa fa-info-circle me-2"></i>{{ detailModalTitle }}</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div v-html="detailModalContent"></div>
                    </div>
                    <div class="modal-footer justify-content-end">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Tutup</button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Delete Modal -->
        <div class="modal fade" id="deleteModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title"><i class="ti-alert me-2"></i>Konfirmasi Hapus</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Apakah Anda yakin ingin menghapus kecamatan <strong>{{ deleteName }}</strong>?</p>
                        <p class="text-danger small mb-3">Tindakan ini tidak dapat dibatalkan.</p>
                        
                        <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                            <input class="form-check-input" type="checkbox" id="confirmCheck" v-model="deleteConfirmedCheck">
                            <label class="form-check-label text-dark" for="confirmCheck">
                                Saya mengerti dan yakin ingin menghapus data ini.
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Batal</button>
                        <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck" @click="submitDelete">
                            <i class="fa fa-trash me-2"></i>Ya, Hapus
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error/Warning Modal -->
        <div class="modal fade" id="errorModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header text-white" :class="modalType === 'warning' ? 'bg-warning text-dark' : 'bg-danger'">
                        <h5 class="modal-title">
                            <i class="me-2" :class="modalType === 'warning' ? 'fa fa-exclamation-triangle' : 'ti-alert'"></i>
                            {{ modalType === 'warning' ? 'Peringatan' : 'Peringatan Validasi' }}
                        </h5>
                        <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut sebelum menyimpan:</p>
                        <p class="mb-3" v-else>Informasi sistem:</p>
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item" :class="modalType === 'warning' ? 'list-group-item-warning' : 'list-group-item-danger'" v-for="(error, index) in errorModalContent" :key="index">
                                <i class="fa me-2" :class="modalType === 'warning' ? 'fa-info-circle' : 'fa-exclamation-circle'"></i>{{ error }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn" :class="modalType === 'warning' ? 'btn-warning' : 'btn-danger'" data-bs-dismiss="modal"><i class="fa fa-check-square-o me-2"></i>Mengerti</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
